import React from 'react';
import BaseSkeleton from './base-skeleton';
import SkeletonAvatar from './skeleton-avatar';
import SkeletonText from './skeleton-text';
import SkeletonFeed from './skeleton-feed';
import { useTheme } from '../../context/theme-context';

interface PageSkeletonProps {
  type?: 'home' | 'profile' | 'search' | 'community';
}

const PageSkeleton: React.FC<PageSkeletonProps> = ({ type = 'home' }) => {
  const { isDarkMode } = useTheme();
  
  const pageBgClass = isDarkMode ? 'bg-neutral-800' : 'bg-white';
  const sectionBgClass = isDarkMode ? 'bg-neutral-800' : 'bg-white';

  const renderHomeSkeleton = () => (
    <main className={`${pageBgClass} min-h-screen`}>
      {/* Header skeleton */}
      <section className={`${sectionBgClass} px-2 py-2`}>
        <div className="flex gap-2 items-center">
          <BaseSkeleton variant="rounded" width="24px" height="24px" />
          <BaseSkeleton variant="rounded" className="flex-1" height="40px" />
          <SkeletonAvatar size="md" />
        </div>
      </section>

      {/* Communities section skeleton */}
      <section className={`${sectionBgClass} pt-2`}>
        <div className="px-5 flex justify-between items-center">
          <BaseSkeleton variant="text" width="150px" height="24px" />
          <BaseSkeleton variant="rounded" width="80px" height="32px" />
        </div>
        
        <div className="flex gap-2 overflow-x-scroll mt-4 pb-5 px-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="min-w-[150px]">
              <BaseSkeleton variant="rounded" height="120px" className="w-full" />
            </div>
          ))}
        </div>
      </section>

      {/* Feed skeleton */}
      <section className="mt-4">
        <SkeletonFeed count={3} variant="post" />
      </section>
    </main>
  );

  const renderProfileSkeleton = () => (
    <div className={`${pageBgClass} min-h-screen`}>
      {/* Cover photo */}
      <BaseSkeleton variant="rectangular" height="200px" className="w-full" />
      
      {/* Avatar - positioned over cover */}
      <div className="px-4 relative">
        <div className="absolute -top-12">
          <SkeletonAvatar size="xl" />
        </div>
      </div>
      
      {/* Profile info */}
      <div className="mt-16 px-4">
        <SkeletonText lines={3} spacing="space-y-2" />
      </div>
      
      {/* Tabs */}
      <div className="flex gap-4 mt-6 px-4 border-b dark:border-gray-700">
        <BaseSkeleton variant="rounded" width="80px" height="40px" />
        <BaseSkeleton variant="rounded" width="80px" height="40px" />
        <BaseSkeleton variant="rounded" width="80px" height="40px" />
      </div>
      
      {/* Content */}
      <div className="mt-4">
        <SkeletonFeed count={2} variant="post" />
      </div>
    </div>
  );

  const renderSearchSkeleton = () => (
    <div className={`${pageBgClass} min-h-screen p-4`}>
      {/* Search bar */}
      <BaseSkeleton variant="rounded" height="48px" className="w-full mb-4" />
      
      {/* Filter chips */}
      <div className="flex gap-2 mb-6 overflow-x-scroll">
        {Array.from({ length: 6 }).map((_, index) => (
          <BaseSkeleton key={index} variant="rounded" width="80px" height="32px" />
        ))}
      </div>
      
      {/* Search results */}
      <SkeletonFeed count={4} variant="user" />
    </div>
  );

  const renderCommunitySkeleton = () => (
    <div className={`${pageBgClass} min-h-screen`}>
      {/* Community header */}
      <div className="relative">
        <BaseSkeleton variant="rectangular" height="150px" className="w-full" />
        <div className="absolute -bottom-12 left-4">
          <SkeletonAvatar size="xl" />
        </div>
      </div>
      
      {/* Community info */}
      <div className="mt-16 px-4">
        <SkeletonText lines={4} spacing="space-y-2" />
      </div>
      
      {/* Join button */}
      <div className="px-4 mt-4">
        <BaseSkeleton variant="rounded" width="120px" height="40px" />
      </div>
      
      {/* Posts */}
      <div className="mt-6">
        <SkeletonFeed count={3} variant="post" />
      </div>
    </div>
  );

  switch (type) {
    case 'home':
      return renderHomeSkeleton();
    case 'profile':
      return renderProfileSkeleton();
    case 'search':
      return renderSearchSkeleton();
    case 'community':
      return renderCommunitySkeleton();
    default:
      return renderHomeSkeleton();
  }
};

export default PageSkeleton;